import {CoreDataType} from "../../../../common/apis/datatype/DataTypeDefinitionDto";
import {TranslationFunction} from "../../../../store/localization/TranslMessasge";
import {StereotypesTranslationKey} from "../stereotypes/StereotypesTranslationKey";
import {DateTimeParser} from "../../../../common/DateTimeParser";


type CoreDataTypeValidationFunc = (value: string, t: TranslationFunction) => string | undefined;

export class CoreDataTypeValidator {

    private readonly coreValidatorRegistry: Map<CoreDataType, CoreDataTypeValidationFunc>;

    constructor() {
        this.coreValidatorRegistry = new Map<CoreDataType, CoreDataTypeValidationFunc>([
                [CoreDataType.BOOLEAN, this.validateBoolean],
                [CoreDataType.CURRENCY, this.validateCurrency],
                [CoreDataType.DATE, this.validateDate],
                [CoreDataType.DATETIME, this.validateDateTime],
                [CoreDataType.FLOAT, this.validateFloat],
                [CoreDataType.INTEGER, this.validateInteger],
                [CoreDataType.STRING, this.validateString],
                [CoreDataType.TIME, this.validateTime],
        ]);
    }

    validateValue(value: string, coreDataType: CoreDataType, t: TranslationFunction): string | undefined {
        const validationFunc = this.coreValidatorRegistry.get(coreDataType);
        if (validationFunc) {
            return validationFunc(value, t);
        } else {
            throw new Error(`No validator found for core data type ${coreDataType}`);
        }
    }

    private validateString(): string | undefined {
        return undefined;
    }

    private validateInteger(value: string, t: TranslationFunction): string | undefined {
        if (value.trim() === '') {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_INTEGER);
        }
        const trimmedValue = value.trim();
        const parsedValue = Number(trimmedValue);
        if (!Number.isInteger(parsedValue) || !Number.isSafeInteger(parsedValue)) {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_INTEGER);
        }
        return undefined;
    }

    private validateFloat(value: string, t: TranslationFunction): string | undefined {
        if (value.trim() === '') {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_FLOAT);
        }
        const trimmedValue = value.trim();
        const parsedValue = Number(trimmedValue);
        if (isNaN(parsedValue) || !isFinite(parsedValue)) {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_FLOAT);
        }
        return undefined;
    }

    private validateCurrency(value: string, t: TranslationFunction): string | undefined {
        if (value.trim() === '') {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_CURRENCY);
        }
        const trimmedValue = value.trim();
        const parsedValue = Number(trimmedValue);
        if (isNaN(parsedValue) || !isFinite(parsedValue)) {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_CURRENCY);
        }
        return undefined;
    }

    private validateBoolean(value: string, t: TranslationFunction): string | undefined {
        if (value.trim() === '') {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_BOOLEAN);
        }
        const trimmedValue = value.trim().toLowerCase();
        if (trimmedValue !== 'true' && trimmedValue !== 'false') {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_BOOLEAN);
        }
        return undefined;
    }

    private validateDate(value: string, t: TranslationFunction): string | undefined {
        const parsedValue = DateTimeParser.parseDate(value);
        if (!parsedValue.isValid()) {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_DATE);
        } else {
            return undefined;
        }
    }

    private validateDateTime(value: string, t: TranslationFunction): string | undefined {
        const parsedValue = DateTimeParser.parseDateTime(value);
        if (!parsedValue.isValid()) {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_DATE_TIME);
        } else {
            return undefined;
        }
    }

    private validateTime(value: string, t: TranslationFunction): string | undefined {
        const parsedValue = DateTimeParser.parseTime(value);
        if (!parsedValue.isValid()) {
            return t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_TIME);
        } else {
            return undefined;
        }
    }
}